import { useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { da } from "date-fns/locale";
import { Calendar as CalIcon } from "lucide-react";

type Booking = {
  id: string;
  start_at: string;
  end_at: string;
  status: string;
  total_amount: number;
  listings: {
    title: string;
    address: string;
  } | null;
  users: {
    name: string;
    email: string;
  } | null;
};

interface BookingsCalendarProps {
  userId: string;
}

export default function BookingsCalendar({ userId }: BookingsCalendarProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());

  // Hent bookinger for ejeren (både som udlejer og som lejer)
  const { data: bookings = [], isLoading } = useQuery({
    queryKey: ["calendar-bookings", userId],
    queryFn: async () => {
      // Bookinger hvor brugeren er udlejer
      const { data: ownerBookings } = await supabase
        .from("bookings")
        .select(`
          *,
          listings!inner(title, address, owner_id),
          users!bookings_renter_id_fkey(name, email)
        `)
        .eq("listings.owner_id", userId)
        .order("start_at", { ascending: true });

      // Bookinger hvor brugeren er lejer
      const { data: renterBookings } = await supabase
        .from("bookings")
        .select(`
          *,
          listings(title, address),
          users!bookings_renter_id_fkey(name, email)
        `)
        .eq("renter_id", userId)
        .order("start_at", { ascending: true });

      return [...(ownerBookings || []), ...(renterBookings || [])] as Booking[];
    },
  });

  // Find bookinger for valgt dato
  const bookingsForSelectedDate = bookings.filter((b) => {
    const start = new Date(b.start_at);
    const end = new Date(b.end_at);
    const selected = selectedDate || new Date();
    
    return selected >= start && selected <= end;
  });

  // Highlight datoer med bookinger
  const bookedDates = bookings.map((b) => {
    const start = new Date(b.start_at);
    const end = new Date(b.end_at);
    const dates: Date[] = [];
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      dates.push(new Date(d));
    }
    
    return dates;
  }).flat();

  const getStatusColor = (status: string) => {
    switch (status) {
      case "confirmed":
      case "completed":
        return "default";
      case "pending":
        return "secondary";
      case "cancelled":
        return "destructive";
      default:
        return "outline";
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Kalender */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalIcon className="h-5 w-5" />
            Booking Kalender
          </CardTitle>
        </CardHeader>
        <CardContent className="flex justify-center">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={setSelectedDate}
            locale={da}
            className="rounded-md border"
            modifiers={{
              booked: bookedDates,
            }}
            modifiersStyles={{
              booked: {
                backgroundColor: "hsl(var(--primary) / 0.1)",
                fontWeight: "bold",
                color: "hsl(var(--primary))",
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Bookinger for valgt dato */}
      <Card>
        <CardHeader>
          <CardTitle>
            {selectedDate 
              ? `Bookinger for ${format(selectedDate, "d. MMMM yyyy", { locale: da })}`
              : "Vælg en dato"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-muted-foreground">Henter bookinger...</p>
          ) : bookingsForSelectedDate.length === 0 ? (
            <p className="text-muted-foreground">Ingen bookinger denne dag</p>
          ) : (
            <div className="space-y-4">
              {bookingsForSelectedDate.map((booking) => (
                <div
                  key={booking.id}
                  className="p-4 border rounded-lg space-y-2 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-1 flex-1">
                      <div className="font-semibold">
                        {booking.listings?.title || "Ukendt plads"}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {booking.listings?.address}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Lejer: {booking.users?.name || booking.users?.email || "Ukendt"}
                      </div>
                      <div className="text-sm font-medium">
                        {format(new Date(booking.start_at), "HH:mm", { locale: da })} -{" "}
                        {format(new Date(booking.end_at), "HH:mm", { locale: da })}
                      </div>
                      <div className="text-sm font-medium">
                        {Math.round(booking.total_amount / 100)} kr
                      </div>
                    </div>
                    <Badge variant={getStatusColor(booking.status)}>
                      {booking.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
